import { useRef, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { connect } from 'react-redux';
import { 
    Card, 
    CardBody, 
    Col, 
    Label, 
    Row, 
    Button, 
    Spinner,
    UncontrolledPopover,
    PopoverBody
} from 'reactstrap';
import { AvCheckbox, AvCheckboxGroup, AvField, AvForm } from 'availity-reactstrap-validation';
import Dropzone from "react-dropzone";
import { withRouter, Link } from "react-router-dom";
import { useMutation } from '@tanstack/react-query';
import DocumentsService from 'src/modules/data-protection/apis/AppDocumentsService';
import { toast } from 'react-toastify';
import { AppDocumentsTypes } from 'src/modules/data-protection/constants';

const CreateDocument = ({
    onCreateSuccessfull,

    t,
    organization
}) => {
    const [ file, setFile ] = useState({
        showError: false,
        src: null
    })

    const createFormRef = useRef();

    const {
        mutate: mutateCreateDocument,
        isLoading: creatingDocumentInProcess
    } = useMutation({
        mutationFn: async ({
            formData
        }) => {
            return await (DocumentsService.getInstance()).create(formData);
        },
        onSuccess: () => {
            onCreateSuccessfull && onCreateSuccessfull();

            createFormRef?.current && createFormRef.current.reset();

            setFile({
                showError: false,
                src: null
            });

            toast(t("Data saved successfully."), {
                type: "success"
            });
        },
        onError: (error) => {
            const data = error.response.data;

            toast(t(data.message || data.title), {
                type: "error"
            });
        }
    });

    const handleCreateDocumentSubmit = (_e, values) => {
        if(!file.src){
            return setFile({
                showError: true,
                src: null
            });
        }

        const formData = new FormData();
        formData.append('title', values.title);
        formData.append('language', values.language);
        formData.append('type', values.type);
        formData.append('src', file.src);

        if(values.role?.length > 0){
            formData.append('role', values.role[0]);
        }

        mutateCreateDocument({
            formData
        })
    }

    const handleAcceptedFiles = (files) => {
        if(files.length > 0){
            const file = files[0]

            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: file.size,
            });

            setFile({
                showError: false,
                src: file
            })
        }
    }

    return (
        <AvForm 
            className="needs-validation" 
            onValidSubmit={handleCreateDocumentSubmit}
            ref={ createFormRef }
        >
            <Card>
                <CardBody>
                    <Row>
                        <Col xs='6' lg='3'>
                            <div className="mb-3">
                                <Label className="form-label">
                                    {t("Title")}
                                </Label>
                                <AvField
                                    type="text"
                                    name="title"
                                    placeholder=""
                                    className="form-control"
                                    errorMessage={t("This field cannot be blank")}
                                    validate={{ required: { value: true } }}
                                />
                            </div>
                        </Col>
                        
                        <Col xs='6' lg='3'>
                            <div className="mb-3">
                                <Label className="form-label">
                                    {t("Type")}
                                </Label>
                                <AvField 
                                    type="select"
                                    name="type"
                                    className="form-control"
                                    validate={{ required: { value: true } }}
                                    errorMessage={t("This field cannot be blank")}
                                >
                                    <option value="" disabled>
                                        {t("Select an item")}
                                    </option>

                                    {AppDocumentsTypes.map((dType) => {
                                        return (
                                            <option value={dType.typeKey}>
                                                {t(dType.title)}
                                            </option>
                                        )
                                    })}
                                </AvField>
                            </div>
                        </Col>

                        <Col xs='6' lg='3'>
                            <div className="mb-3">
                                <Label className="form-label">
                                    {t("Language")}
                                </Label>

                                <AvField
                                    type="select"
                                    name="language"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{ required: { value: true } }}
                                >
                                    <option value="" />
                                    {organization.languages.map((language) => {
                                        return (
                                            <option
                                                key={language.language.id}
                                                value={language.language.id}
                                            >
                                                {t(language.name)}
                                            </option>
                                        );
                                    })}
                                </AvField>
                            </div>
                        </Col>

                        <Col xs='6' lg='3'>
                            <div className='mb-3 pt-4 d-flex gap-1 align-items-center'>
                                <AvCheckboxGroup
                                    name="role"
                                >
                                    <AvCheckbox
                                        label={t('Show in third party view')}
                                        value="ROLE_THIRD_PARTY"
                                    />
                                </AvCheckboxGroup>

                                <Button
                                    id='show-in-thirdparty-help'
                                    type='button'
                                    color='link'
                                >
                                    <i className='ri-information-line font-size-18'></i>
                                </Button>

                                <UncontrolledPopover 
                                    trigger="click" 
                                    placement="bottom" 
                                    target="show-in-thirdparty-help"
                                >
                                    <PopoverBody>
                                        {t('Please select this box if you would like to show this document in the third party view. If not selected the document will be available for employees only via the admin and employee view.')}
                                    </PopoverBody>
                                </UncontrolledPopover>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <div className="mb-3">
                                <Label className="form-label">
                                    {`${t("Upload a new file")} (PDF)`}
                                </Label>
                                <Dropzone
                                    onDrop={handleAcceptedFiles}
                                    maxFiles={1}
                                    accept=".pdf"
                                    multiple={false}
                                    required
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <div className="dropzone">
                                            <div
                                                className="dz-message needsclick"
                                                {...getRootProps()}
                                            >
                                                <input {...getInputProps()} />
                                                <div className="mb-3">
                                                    <i className="display-4 text-muted ri-upload-cloud-2-line"></i>
                                                </div>
                                                <h4>
                                                    {t("Drop files here or click to upload")}
                                                </h4>

                                                {file.showError && (
                                                    <p className='text-danger font-size-14'>
                                                        {t("This field cannot be blank")}
                                                    </p>
                                                )}                                                    
                                            </div>
                                        </div>
                                    )}
                                </Dropzone>

                                <div className="dropzone-previews mt-3" id="file-previews">
                                    {file.src && (
                                        <Card
                                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                        >
                                            <div className="p-3">
                                                <Row className="align-items-center">
                                                    <Col>
                                                        <Link to="#" className="text-muted fw-bold">
                                                            {file.src.name}
                                                        </Link>
                                                        <p className="mb-0">
                                                            <strong>{file.src.formattedSize}</strong>
                                                        </p>
                                                    </Col>
                                                    <Col xs={2} style={{ textAlign: "right" }}>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => setFile({
                                                                showError: true,
                                                                src: null
                                                            })}
                                                        >
                                                            X
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Card>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col xs={12}>
                            <div>
                                <Button
                                    color="primary"
                                    type="submit"
                                    disabled={creatingDocumentInProcess}
                                >
                                    {creatingDocumentInProcess && (
                                        <Spinner color='white' size='sm' className='me-1' />
                                    )}

                                    {t("Upload")}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </AvForm>
    );
};

const mapStatetoProps = (state) => {
	const { token, user } = state.Login;
	const { Organization } = state;
	
	return {
		token,
		user,
		organization: Organization
	};
};

export default withNamespaces()(
    withRouter(
        connect(mapStatetoProps, {
        })(
            CreateDocument
        )
    )
);